import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, File, Trash2, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import './BatchFileUploadModal.css';

export type BatchUploadStatus = 'idle' | 'uploading' | 'success' | 'error' | 'partial';

export interface FileUploadState {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

/**
 * Result for a single file upload within a batch.
 * If onUpload returns this array, the modal will show per-file success/error states.
 */
export interface FileUploadResult {
  /** The filename (should match file.name from the uploaded File) */
  filename: string;
  /** Whether this file uploaded successfully */
  success: boolean;
  /** Error message if success is false */
  error?: string;
}

export interface BatchFileUploadModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /**
   * Handler for batch file upload - receives files array and optional comment.
   * Can optionally return an array of FileUploadResult for per-file status.
   * If void/undefined is returned, all files are marked as success.
   * If results are returned, each file's status is set based on its result.
   */
  onUpload: (files: File[], comment: string) => Promise<void | FileUploadResult[]>;
  /** Modal title */
  title?: string;
  /** Drop zone text */
  dropText?: string;
  /** Allowed file types text */
  allowedTypesText?: string;
  /** Comment label */
  commentLabel?: string;
  /** Comment placeholder */
  commentPlaceholder?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Upload button text */
  uploadText?: string;
  /** Uploading button text */
  uploadingText?: string;
  /** Success message */
  successMessage?: string;
  /** Error message (override) */
  errorMessage?: string;
  /** Close immediately after successful upload without showing confirmation */
  closeOnSuccess?: boolean;
  /** Maximum file size in bytes (default: 20MB) */
  maxFileSize?: number;
  /** Maximum number of files (default: 50) */
  maxFiles?: number;
  /** Allowed MIME types */
  allowedMimeTypes?: string[];
  /** Allowed file extensions */
  allowedExtensions?: string[];
  /** Custom className */
  className?: string;
  /** Files selected text (with count placeholder) */
  filesSelectedText?: string;
  /** Clear all button text */
  clearAllText?: string;
  /** Remove file aria label */
  removeFileLabel?: string;
  /** Add more files button text */
  addMoreText?: string;
  /** Partial success message (with {success} and {total} placeholders) */
  partialSuccessMessage?: string;
}

const DEFAULT_ALLOWED_TYPES = [
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/rtf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/pdf',
  'image/png',
  'image/jpeg',
];

const DEFAULT_EXTENSIONS = [
  '.txt', '.md', '.csv', '.rtf', '.doc', '.docx',
  '.xls', '.xlsx', '.ppt', '.pptx', '.pdf', '.png', '.jpg', '.jpeg'
];

const DEFAULT_MAX_SIZE = 20 * 1024 * 1024; // 20MB
const DEFAULT_MAX_FILES = 50;

let fileIdCounter = 0;
const generateFileId = () => `file-${++fileIdCounter}-${Date.now()}`;

/**
 * Recursively reads all files from a dropped directory entry
 */
async function readDirectoryEntry(entry: FileSystemDirectoryEntry): Promise<File[]> {
  const files: File[] = [];
  const reader = entry.createReader();

  // Read all entries in batches (readEntries may not return all at once)
  const readAllEntries = async (): Promise<FileSystemEntry[]> => {
    const entries: FileSystemEntry[] = [];
    let batch: FileSystemEntry[];
    do {
      batch = await new Promise((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      entries.push(...batch);
    } while (batch.length > 0);
    return entries;
  };

  const entries = await readAllEntries();

  for (const childEntry of entries) {
    if (childEntry.isFile) {
      const file = await new Promise<File>((resolve, reject) => {
        (childEntry as FileSystemFileEntry).file(resolve, reject);
      });
      files.push(file);
    } else if (childEntry.isDirectory) {
      const subFiles = await readDirectoryEntry(childEntry as FileSystemDirectoryEntry);
      files.push(...subFiles);
    }
  }

  return files;
}

/**
 * Process dropped items - handles both files and folders via DataTransferItemList
 */
async function processDroppedItems(dataTransfer: DataTransfer): Promise<File[]> {
  const files: File[] = [];
  const items = dataTransfer.items;

  // Try to use webkitGetAsEntry for folder support
  if (items && items.length > 0) {
    const entryPromises: Promise<File[]>[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.();
        if (entry) {
          if (entry.isDirectory) {
            entryPromises.push(readDirectoryEntry(entry as FileSystemDirectoryEntry));
          } else if (entry.isFile) {
            const file = item.getAsFile();
            if (file) {
              files.push(file);
            }
          }
        } else {
          // Fallback for browsers without webkitGetAsEntry
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
    }

    const nestedFiles = await Promise.all(entryPromises);
    for (const nested of nestedFiles) {
      files.push(...nested);
    }
  } else if (dataTransfer.files.length > 0) {
    // Fallback to files array
    files.push(...Array.from(dataTransfer.files));
  }

  return files;
}

/**
 * BatchFileUploadModal - A modal component for uploading multiple files with drag & drop
 *
 * Click behavior: Opens file picker for selecting files
 * Drag & drop: Accepts both files and folders (folders are read recursively)
 */
export const BatchFileUploadModal: React.FC<BatchFileUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title = 'Upload Documents',
  dropText = 'Drop files here or click to browse',
  allowedTypesText = 'PDF, Word, Excel, Images (max. 20MB each)',
  commentLabel = 'Comment (optional)',
  commentPlaceholder = 'Add a comment...',
  cancelText = 'Cancel',
  uploadText = 'Upload',
  uploadingText = 'Uploading...',
  successMessage = 'Files uploaded successfully!',
  errorMessage,
  closeOnSuccess = false,
  maxFileSize = DEFAULT_MAX_SIZE,
  maxFiles = DEFAULT_MAX_FILES,
  allowedMimeTypes = DEFAULT_ALLOWED_TYPES,
  allowedExtensions = DEFAULT_EXTENSIONS,
  className = '',
  filesSelectedText = '{count} files selected',
  clearAllText = 'Clear all',
  removeFileLabel = 'Remove file',
  addMoreText: _addMoreText = 'Add more',
  partialSuccessMessage = '{success} of {total} files uploaded successfully',
}) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<BatchUploadStatus>('idle');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setFiles([]);
    setComment('');
    setError(null);
    setStatus('idle');
    setDragOver(false);
    fileIdCounter = 0;
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize) {
      return `File too large. Maximum size is ${formatFileSize(maxFileSize)}`;
    }

    const isValidType = allowedMimeTypes.includes(file.type) ||
      allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidType) {
      return 'File type not supported';
    }

    return null;
  };

  const addFiles = useCallback((newFiles: File[]) => {
    // Check max files limit
    const totalFiles = files.length + newFiles.length;
    if (totalFiles > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed. You selected ${newFiles.length} files but only ${maxFiles - files.length} more can be added.`);
      return;
    }

    const validFiles: FileUploadState[] = [];
    const errors: string[] = [];

    for (const file of newFiles) {
      // Skip hidden files and system files
      if (file.name.startsWith('.')) continue;

      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        // Check for duplicates
        const isDuplicate = files.some(f => f.file.name === file.name && f.file.size === file.size);
        if (!isDuplicate) {
          validFiles.push({
            file,
            id: generateFileId(),
            status: 'pending',
            progress: 0,
          });
        }
      }
    }

    if (errors.length > 0) {
      setError(`Some files could not be added:\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? `\n...and ${errors.length - 3} more` : ''}`);
    } else {
      setError(null);
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      setStatus('idle');
    }
  }, [files, maxFiles, maxFileSize, allowedMimeTypes, allowedExtensions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      addFiles(Array.from(selectedFiles));
    }
    // Reset input so same files can be selected again
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    try {
      // Process dropped items - supports both files and folders
      const droppedFiles = await processDroppedItems(e.dataTransfer);
      if (droppedFiles.length > 0) {
        addFiles(droppedFiles);
      }
    } catch (err) {
      console.error('Error processing dropped items:', err);
      // Fallback to simple file list
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        addFiles(Array.from(droppedFiles));
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setError(null);
  };

  const handleClearAll = () => {
    setFiles([]);
    setError(null);
    setStatus('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    try {
      setStatus('uploading');
      setError(null);

      // Mark all files as uploading
      setFiles(prev => prev.map(f => ({ ...f, status: 'uploading' as const, progress: 0 })));

      const filesToUpload = files.map(f => f.file);
      const results = await onUpload(filesToUpload, comment);

      // Handle per-file results if returned
      if (results && Array.isArray(results)) {
        // Create a map of filename -> result for quick lookup
        const resultMap = new Map(results.map(r => [r.filename, r]));

        setFiles(prev => prev.map(f => {
          const result = resultMap.get(f.file.name);
          if (result) {
            return {
              ...f,
              status: result.success ? 'success' as const : 'error' as const,
              progress: result.success ? 100 : 0,
              error: result.error,
            };
          }
          // If no result for this file, assume success (backwards compatibility)
          return { ...f, status: 'success' as const, progress: 100 };
        }));

        const successCount = results.filter(r => r.success).length;
        const totalCount = results.length;

        if (successCount === totalCount) {
          // All succeeded
          if (closeOnSuccess) {
            handleClose();
          } else {
            setStatus('success');
            setTimeout(() => {
              handleClose();
            }, 1500);
          }
        } else if (successCount === 0) {
          // All failed
          setStatus('error');
          setError(errorMessage || 'All files failed to upload');
        } else {
          // Partial success
          setStatus('partial');
          setError(partialSuccessMessage
            .replace('{success}', String(successCount))
            .replace('{total}', String(totalCount)));
        }
      } else {
        // No results returned - assume all success (backwards compatibility)
        setFiles(prev => prev.map(f => ({ ...f, status: 'success' as const, progress: 100 })));

        if (closeOnSuccess) {
          handleClose();
        } else {
          setStatus('success');
          setTimeout(() => {
            handleClose();
          }, 1500);
        }
      }
    } catch (err) {
      setStatus('error');
      setFiles(prev => prev.map(f => ({ ...f, status: 'error' as const, progress: 0 })));
      setError(errorMessage || (err instanceof Error ? err.message : 'Upload failed'));
    }
  };

  if (!isOpen) return null;

  const acceptExtensions = allowedExtensions.join(',');
  const isUploading = status === 'uploading';
  const isSuccess = status === 'success';
  const hasFiles = files.length > 0;
  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);

  return (
    <div className="batch-upload-modal-overlay" onClick={handleClose}>
      <div
        className={`batch-upload-modal ${className}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="batch-upload-modal-title"
      >
        {/* Header */}
        <div className="batch-upload-modal-header">
          <h2 id="batch-upload-modal-title" className="batch-upload-modal-title">
            {title}
          </h2>
          <button
            className="batch-upload-modal-close"
            onClick={handleClose}
            aria-label="Close"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="batch-upload-modal-body">
          {/* Drop Zone */}
          <div
            className={`batch-upload-dropzone ${dragOver ? 'batch-upload-dropzone--drag-over' : ''} ${hasFiles ? 'batch-upload-dropzone--has-files' : ''} ${isSuccess ? 'batch-upload-dropzone--success' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !isUploading && !hasFiles && fileInputRef.current?.click()}
            role={!hasFiles ? "button" : undefined}
            tabIndex={!hasFiles ? 0 : undefined}
            onKeyDown={(e) => {
              if (!hasFiles && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                !isUploading && fileInputRef.current?.click();
              }
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleInputChange}
              accept={acceptExtensions}
              multiple
              hidden
              disabled={isUploading}
            />

            {isSuccess ? (
              <div className="batch-upload-success">
                <CheckCircle className="batch-upload-success-icon" size={48} />
                <p className="batch-upload-success-text">{successMessage}</p>
              </div>
            ) : hasFiles ? (
              <div className="batch-upload-file-list-container">
                {/* File list header */}
                <div className="batch-upload-file-list-header">
                  <span className="batch-upload-file-count">
                    {filesSelectedText.replace('{count}', String(files.length))}
                    <span className="batch-upload-total-size">({formatFileSize(totalSize)})</span>
                  </span>
                  {!isUploading && (
                    <button
                      type="button"
                      className="batch-upload-clear-all"
                      onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                    >
                      {clearAllText}
                    </button>
                  )}
                </div>

                {/* Scrollable file list */}
                <div className="batch-upload-file-list">
                  {files.map((fileState) => (
                    <div key={fileState.id} className={`batch-upload-file-item batch-upload-file-item--${fileState.status}`}>
                      <div className="batch-upload-file-icon">
                        {fileState.status === 'uploading' ? (
                          <Loader2 size={16} className="batch-upload-spinner" />
                        ) : fileState.status === 'success' ? (
                          <CheckCircle size={16} className="batch-upload-file-success" />
                        ) : fileState.status === 'error' ? (
                          <AlertCircle size={16} className="batch-upload-file-error" />
                        ) : (
                          <File size={16} />
                        )}
                      </div>
                      <div className="batch-upload-file-info">
                        <span className="batch-upload-file-name" title={fileState.file.name}>
                          {fileState.file.name}
                        </span>
                        <span className="batch-upload-file-size">
                          {fileState.error || formatFileSize(fileState.file.size)}
                        </span>
                      </div>
                      {!isUploading && fileState.status === 'pending' && (
                        <button
                          type="button"
                          className="batch-upload-file-remove"
                          onClick={(e) => { e.stopPropagation(); handleRemoveFile(fileState.id); }}
                          aria-label={removeFileLabel}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

              </div>
            ) : (
              <div className="batch-upload-dropzone-content">
                <Upload className="batch-upload-dropzone-icon" size={48} />
                <p className="batch-upload-dropzone-text">{dropText}</p>
                <p className="batch-upload-dropzone-types">{allowedTypesText}</p>
              </div>
            )}

            {/* Progress indicator for uploading state */}
            {isUploading && (
              <div className="batch-upload-progress-overlay">
                <Loader2 size={24} className="batch-upload-spinner" />
                <span>{uploadingText}</span>
              </div>
            )}
          </div>

          {/* Comment Field */}
          <div className="batch-upload-form-group">
            <label htmlFor="batch-upload-comment" className="batch-upload-label">
              {commentLabel}
            </label>
            <textarea
              id="batch-upload-comment"
              className="batch-upload-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={commentPlaceholder}
              rows={2}
              disabled={isUploading || isSuccess}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="batch-upload-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="batch-upload-modal-actions">
            <button
              type="button"
              className="batch-upload-btn batch-upload-btn--secondary"
              onClick={handleClose}
              disabled={isUploading}
            >
              {cancelText}
            </button>
            <button
              type="submit"
              className="batch-upload-btn batch-upload-btn--primary"
              disabled={files.length === 0 || isUploading || isSuccess}
            >
              {isUploading ? uploadingText : `${uploadText} (${files.length})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchFileUploadModal;
